import React from 'react'

import { faChartLine } from '@fortawesome/pro-light-svg-icons/faChartLine'
import { faPaintbrush } from '@fortawesome/pro-light-svg-icons/faPaintbrush'
import { faPiggyBank } from '@fortawesome/pro-light-svg-icons/faPiggyBank'
import { Container, styled, Typography } from '@mui/material'

import { Icon } from '@/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'

interface ValueProp {
  icon: typeof faChartLine
  title: string
  body: string
}

const VALUE_PROPS: ValueProp[] = [
  {
    icon: faChartLine,
    title: 'Cut delivery time by 80%',
    body: 'What used to take 3-5 days of manual illustration takes 30 minutes. Generate consistent characters, lay out spreads, ship the PDF — same afternoon.',
  },
  {
    icon: faPiggyBank,
    title: 'Charge clients $200-500. Tools cost $79.',
    body: 'No per-project licensing, no per-seat fees, no monthly renewals. Margins on every project stay yours.',
  },
  {
    icon: faPaintbrush,
    title: 'Canva-style editor — no Photoshop',
    body: 'Drag-and-drop layouts, in-line text editing, character consistency across spreads. Works in any browser. iPhone and iPad apps too.',
  },
]

export const CreatorsValueProps: React.FC = () => (
  <StyledSection>
    <Container maxWidth="lg">
      <StyledHeader>
        <StyledHeadline variant="h2">
          Why freelancers ship faster on Pixie
        </StyledHeadline>
      </StyledHeader>
      <StyledGrid>
        {VALUE_PROPS.map((prop) => (
          <StyledCard key={prop.title}>
            <StyledIconCircle>
              <Icon icon={prop.icon} sx={{ fontSize: pxToRem(22) }} />
            </StyledIconCircle>
            <StyledTitle variant="h6">{prop.title}</StyledTitle>
            <StyledBody variant="body2">{prop.body}</StyledBody>
          </StyledCard>
        ))}
      </StyledGrid>
    </Container>
  </StyledSection>
)

const StyledSection = styled('section')(({ theme }) => ({
  background: '#FFFFFF',
  padding: theme.spacing(6, 0),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(10, 0),
  },
}))

const StyledHeader = styled('div')(({ theme }) => ({
  textAlign: 'center',
  maxWidth: pxToRem(640),
  margin: '0 auto',
  marginBottom: theme.spacing(5),

  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(7),
  },
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  lineHeight: 1.2,
}))

const StyledGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(4),
  },
}))

const StyledCard = styled('div')(({ theme }) => ({
  padding: theme.spacing(3.5, 3),
  borderRadius: pxToRem(20),
  backgroundColor: '#FAF2EB',
  border: '1px solid rgba(108, 60, 20, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
}))

const StyledIconCircle = styled('div')(({ theme }) => ({
  width: pxToRem(48),
  height: pxToRem(48),
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(0.5),
}))

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  lineHeight: 1.3,
}))

const StyledBody = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  opacity: 0.78,
  fontSize: pxToRem(14),
  lineHeight: 1.6,
}))
